import React, { useLayoutEffect, useRef, useState } from "react";
import { Box, Container, Grid, Stack, Typography } from "@mui/material";

import PopularGamesCard from "../../cards/popularGamesCard";
import LiveGames from "../liveGames/liveGames";
import { initPopularGamesAnimations, initPopularGamesHoverMosaic } from "../../animations";

import powerballImage from "../../../../../static/Images/powerball.png";
import revolverImage from "../../../../../static/Images/revolver.png";
import pokiesImage from "../../../../../static/Images/pookies.png";
import mineImage from "../../../../../static/Images/mine.png";
import gamesBackground from "../../../../../static/Images/games_background.png";

import { colors } from "../../../../../theme";

import "./popularGames.css";

const PopularGames = () => {
  const sectionRef = useRef(null);
  const liveSectionRef = useRef(null);
  const hoverTilesRef = useRef(null);
  const activeHoverImageRef = useRef(null);
  const hoverAnimationIdRef = useRef(0);
  const [hoveredGame, setHoveredGame] = useState(null);

  const games = [
    {
      title: "Powerball",
      type: "LOTTERY",
      description: "Massive jackpots, global participation.",
      infoLabel: "Next Draw",
      infoValue: "12 : 24 : 36",
      image: powerballImage,
      accentColor: colors.ribbon.deltaNegative,
      route: "/all-lottery",
    },
    {
      title: "Revolver",
      type: "SPINNER",
      description: "Pick a slice, spin the wheel, and watch the draw.",
      infoLabel: "Next Spin",
      infoValue: "Ready",
      image: revolverImage,
      accentColor: colors.charts.amber,
      route: "/spinnerlottery",
    },
    {
      title: "Pokies",
      type: "SLOTS",
      description: "Fast-paced games with exciting winning chances.",
      infoLabel: "Play",
      infoValue: "Now",
      image: pokiesImage,
      accentColor: colors.charts.emerald,
      route: "/all-minelottery",
    },
    {
      title: "Mine",
      type: "MINE",
      description: "A faster, higher-risk format for every ticket.",
      infoLabel: "Play",
      infoValue: "Now",
      image: mineImage,
      accentColor: colors.blue[500],
      route: "/all-minelottery",
    },
  ];

  useLayoutEffect(() => {
    return initPopularGamesHoverMosaic({
      tileContainer: hoverTilesRef.current,
      hoveredGame,
      activeHoverImageRef,
      hoverAnimationIdRef,
    });
  }, [hoveredGame]);

  useLayoutEffect(() => {
    return initPopularGamesAnimations({
      sectionRef,
      liveSectionRef,
      setHoveredGame,
    });
  }, []);

  return (
    <>
      <Box
        ref={sectionRef}
        component="section"
        className="home-popular-games"
        sx={{
          position: "relative",
          width: "100%",
          minHeight: "calc(100vh - 60px)",
          height: "calc(100vh - 60px)",
          display: "flex",
          alignItems: "center",
          py: { xs: 3, md: 4 },
          overflow: "hidden",
        }}
      >
        <Box
          className="popular-games-default-background"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            zIndex: 0,
            pointerEvents: "none",
            backgroundImage: `url(${gamesBackground})`,
            backgroundPosition: "center",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            willChange: "opacity",
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              background: (theme) =>
                `linear-gradient(135deg, ${theme.custom.colors.alpha.white[30]}, ${theme.custom.colors.alpha.white[12]})`,
            },
          }}
        />

        <Box
          ref={hoverTilesRef}
          className="popular-games-hover-background"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            zIndex: 1,
            pointerEvents: "none",
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(6, 1fr)",
              md: "repeat(8, 1fr)",
              lg: "repeat(10, 1fr)",
            },
            gridTemplateRows: "repeat(6, 1fr)",
            overflow: "hidden",
            opacity: 1,
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.14), rgba(255,255,255,0.04))",
            },
          }}
        >
          {Array.from({ length: 60 }).map((_, index) => {
            const columns = 10;
            const column = index % columns;
            const row = Math.floor(index / columns);

            return (
              <Box
                key={index}
                className="popular-games-hover-tile"
                data-column={column}
                data-row={row}
                sx={{
                  width: "100%",
                  height: "100%",
                  minWidth: 0,
                  minHeight: 0,
                  backgroundColor: "transparent",
                  opacity: 0,
                  transformOrigin: "center center",
                  position: "relative",
                  overflow: "hidden",
                  "--col": column,
                  "--row": row,
                  "--cols": columns,
                  "--rows": 6,
                }}
              >
                <Box
                  className="popular-games-hover-tile-inner"
                  sx={{
                    position: "absolute",
                    width: "calc(100% * var(--cols))",
                    height: "calc(100% * var(--rows))",
                    left: "calc(var(--col) * -100%)",
                    top: "calc(var(--row) * -100%)",
                    backgroundImage: "inherit",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    pointerEvents: "none",
                  }}
                />
              </Box>
            );
          })}
        </Box>

        <Box
          className="popular-games-content"
          sx={{
            position: "relative",
            zIndex: 2,
            width: "100%",
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              width: "100%",
              maxWidth: "1400px",
              mx: "auto",
              px: { xs: 1.5, sm: 3, md: 5 },
            }}
          >
            <Stack
              className="popular-games-header"
              direction={{ xs: "row", sm: "row" }}
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
              sx={{
                width: "100%",
                mb: { xs: 2, md: 2 },
              }}
            >
              <Box>
                <Typography
                  component="p"
                  className="popular-games-eyebrow"
                  sx={{
                    mb: 0.5,
                    fontSize: { xs: "10px", sm: "12px" },
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "primary.main",
                  }}
                >
                  Diverse games. Real opportunities.
                </Typography>

                <Typography
                  component="h2"
                  className="popular-games-title"
                  sx={{
                    m: 0,
                    fontSize: {
                      xs: "24px",
                      md: "52px",
                      lg: "64px",
                    },
                    lineHeight: 1.05,
                    fontWeight: 800,
                    color: "custom.colors.home.navyDark",
                    whiteSpace: { xs: "nowrap", sm: "normal" },
                  }}
                >
                  {"Popular Games".split("").map((letter, index) => (
                    <Box
                      component="span"
                      key={`${letter}-${index}`}
                      className="popular-games-title-letter"
                      sx={{
                        display: "inline-block",
                        fontSize: "inherit",
                        fontWeight: "inherit",
                        lineHeight: "inherit",
                        whiteSpace: letter === " " ? "pre" : "normal",
                      }}
                    >
                      {letter === " " ? "\u00A0" : letter}
                    </Box>
                  ))}
                </Typography>
              </Box>

              <Box
                component="a"
                href="/all-lottery"
                className="popular-games-link"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: "custom.colors.home.navyDark",
                  textDecoration: "none",
                  fontSize: { xs: "11px", md: "13px" },
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  "&:hover": {
                    color: "primary.main",
                  },
                }}
              >
                View All Games
                <Box component="span">→</Box>
              </Box>
            </Stack>

            <Grid
              container
              className="popular-games-grid"
              spacing={{ xs: 1.25, md: 2 }}
              alignItems="stretch"
            >
              {games.map((game) => (
                <Grid
                  item
                  key={game.title}
                  xs={6}
                  sm={6}
                  md={3}
                  className="popular-games-card"
                  onMouseEnter={() => setHoveredGame(game)}
                  onMouseLeave={() => setHoveredGame(null)}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "stretch",
                  }}
                >
                  <PopularGamesCard
                    image={game.image}
                    title={game.title}
                    type={game.type}
                    description={game.description}
                    infoLabel={game.infoLabel}
                    infoValue={game.infoValue}
                    accentColor={game.accentColor}
                    onClick={() => {
                      window.location.href = game.route;
                    }}
                  />
                </Grid>
              ))}
            </Grid>

            <Box
              className="live-transition-tab"
              sx={{
                position: "relative",
                width: "100%",
                minHeight: "80px",
                mt: { xs: 1.5, md: 2 },
                borderRadius: "20px",
                overflow: "hidden",
                background: (theme) =>
                  `linear-gradient(135deg, ${theme.custom.colors.alpha.white[95]}, rgba(238,247,243,0.94))`,
                border: (theme) =>
                  `1px solid ${theme.custom.colors.alpha.white[95]}`,
                boxShadow: "0 14px 40px rgba(70,100,140,0.13)",
                backdropFilter: "blur(18px)",
                WebkitBackdropFilter: "blur(18px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: { xs: 1.5, md: 4 },
                py: 1,
                boxSizing: "border-box",
                zIndex: 10,
                transformOrigin: "center center",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={{ xs: 1, md: 1.5 }}
                className="live-transition-main"
              >
                <Box
                  className="live-transition-dot"
                  sx={{
                    width: "11px",
                    height: "11px",
                    minWidth: "11px",
                    borderRadius: "50%",
                    backgroundColor: colors.emerald[500],
                    boxShadow:
                      "0 0 0 5px rgba(32,217,151,0.12), 0 0 18px rgba(32,217,151,0.5)",
                    animation: "liveTransitionPulse 1.5s ease-in-out infinite",
                    "@keyframes liveTransitionPulse": {
                      "0%, 100%": {
                        opacity: 1,
                        transform: "scale(1)",
                      },
                      "50%": {
                        opacity: 0.45,
                        transform: "scale(0.72)",
                      },
                    },
                  }}
                />

                <Typography
                  component="span"
                  className="live-transition-label"
                  sx={{
                    fontSize: { xs: "16px", sm: "20px" },
                    lineHeight: 1,
                    fontWeight: 800,
                    letterSpacing: "0.02em",
                    color: "custom.colors.home.navyDark",
                    flexShrink: 0,
                  }}
                >
                  LIVE
                </Typography>

                <Box
                  className="live-transition-text-slot"
                  sx={{
                    position: "relative",
                    display: "inline-flex",
                    alignItems: "center",
                    minWidth: {
                      xs: "80px",
                      md: "250px",
                    },
                    minHeight: "42px",
                  }}
                >
                  <Typography
                    component="span"
                    className="live-transition-text"
                    sx={{
                      fontSize: { xs: "11px", sm: "12px" },
                      display: {
                        xs: "none",
                        sm: "inline-block",
                      },
                      color: "custom.colors.alpha.navy[55]",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      position: "absolute",
                      left: 0,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  >
                    Live lotteries happening right now
                  </Typography>

                  <Typography
                    component="span"
                    className="live-transition-arrow"
                    sx={{
                      fontSize: {
                        xs: "30px",
                        md: "42px",
                      },
                      color: "custom.colors.primary.main",
                      fontWeight: 700,
                      lineHeight: 1,
                      display: "inline-block",
                      position: "absolute",
                      left: 0,
                      top: "50%",
                      transform: "translateY(-50%)",
                      opacity: 0,
                      flexShrink: 0,
                      userSelect: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    →
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Container>
        </Box>
      </Box>

      <Box
        ref={liveSectionRef}
        component="section"
        className="home-live-section"
        sx={{
          position: "relative",
          width: "100%",
          minHeight: "calc(100vh - 60px)",
          height: "calc(100vh - 60px)",
          overflow: "hidden",
        }}
      >
        <LiveGames />
      </Box>
    </>
  );
};

export default PopularGames;